import React, { useState } from 'react';
import { 
  Play, Pause, MoreVertical, Edit, Trash2, Download, 
  ExternalLink, Eye, TrendingUp 
} from 'lucide-react';
import { Card } from '../common/Card';
import { StatusBadge } from '../common/StatusBadge';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Song } from '../../types';

interface SongTableProps {
  songs: Song[];
  onEdit: (song: Song) => void;
  onDelete: (songId: string) => void;
  onViewDetails: (song: Song) => void;
}

export const SongTable: React.FC<SongTableProps> = ({
  songs,
  onEdit,
  onDelete,
  onViewDetails
}) => {
  const [selectedSongs, setSelectedSongs] = useState<string[]>([]);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);
  const [showActionMenu, setShowActionMenu] = useState<string | null>(null);

  const formatDuration = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatRevenue = (amount: number) => {
    return `$${amount.toLocaleString()}`;
  };

  const handlePlayPause = (songId: string) => {
    if (currentlyPlaying === songId) {
      setCurrentlyPlaying(null);
    } else {
      setCurrentlyPlaying(songId);
    }
  };

  const handleSelectSong = (songId: string) => {
    setSelectedSongs(prev => 
      prev.includes(songId) 
        ? prev.filter(id => id !== songId)
        : [...prev, songId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSongs.length === songs.length) {
      setSelectedSongs([]);
    } else {
      setSelectedSongs(songs.map(song => song.id));
    }
  };

  const confirmDelete = (songId: string) => {
    onDelete(songId);
    setShowDeleteModal(null);
  };

  return (
    <div className="space-y-4">
      {/* Bulk Actions */}
      {selectedSongs.length > 0 && (
        <Card className="p-4 bg-gold-500/10 border-gold-500/20">
          <div className="flex items-center justify-between">
            <p className="text-gold-400 font-medium">
              {selectedSongs.length} song{selectedSongs.length > 1 ? 's' : ''} selected
            </p>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm">
                Update Metadata
              </Button>
              <Button variant="outline" size="sm">
                Remove from Stores
              </Button>
              <Button variant="danger" size="sm">
                Delete Selected
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Table */}
      <Card padding="none" className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-black-800 border-b border-gray-700">
              <tr>
                <th className="p-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedSongs.length === songs.length}
                    onChange={handleSelectAll}
                    className="rounded border-gray-600 bg-black-700 text-gold-500 focus:ring-gold-500"
                  />
                </th>
                <th className="p-4 text-left text-sm font-medium text-gray-300">Song</th>
                <th className="p-4 text-left text-sm font-medium text-gray-300">Status</th>
                <th className="p-4 text-left text-sm font-medium text-gray-300">Stores</th>
                <th className="p-4 text-left text-sm font-medium text-gray-300">Streams</th>
                <th className="p-4 text-left text-sm font-medium text-gray-300">Revenue</th>
                <th className="p-4 text-left text-sm font-medium text-gray-300">Release Date</th>
                <th className="p-4 text-left text-sm font-medium text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {songs.map((song) => (
                <tr key={song.id} className="hover:bg-gray-800/50 transition-colors">
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedSongs.includes(song.id)}
                      onChange={() => handleSelectSong(song.id)}
                      className="rounded border-gray-600 bg-black-700 text-gold-500 focus:ring-gold-500"
                    />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="relative flex-shrink-0">
                        <img
                          src={song.coverArt}
                          alt={song.title}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                        <button
                          onClick={() => handlePlayPause(song.id)}
                          className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                        >
                          {currentlyPlaying === song.id ? (
                            <Pause className="w-4 h-4 text-white" />
                          ) : (
                            <Play className="w-4 h-4 text-white" />
                          )}
                        </button>
                      </div>
                      <div>
                        <p className="font-medium text-white">{song.title}</p>
                        <p className="text-sm text-gray-400">{song.artist}</p>
                        <p className="text-xs text-gray-500">{formatDuration(song.duration)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <StatusBadge status={song.status} />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-1">
                      <span className="text-white font-medium">{song.stores.length}</span>
                      <span className="text-gray-400 text-sm">platforms</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-1">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <span className="text-white font-medium">{formatNumber(song.streams)}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-green-400 font-medium">
                      {formatRevenue(song.revenue)}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="text-gray-300">
                      {new Date(song.releaseDate).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="relative">
                      <button
                        onClick={() => setShowActionMenu(showActionMenu === song.id ? null : song.id)}
                        className="p-1 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      
                      {showActionMenu === song.id && (
                        <div className="absolute right-0 top-8 bg-black-800 border border-gray-700 rounded-lg shadow-lg z-10 min-w-48">
                          <button
                            onClick={() => {
                              onViewDetails(song);
                              setShowActionMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center space-x-2"
                          >
                            <Eye className="w-4 h-4" />
                            <span>View Details</span>
                          </button>
                          <button
                            onClick={() => {
                              onEdit(song);
                              setShowActionMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center space-x-2"
                          >
                            <Edit className="w-4 h-4" />
                            <span>Edit</span>
                          </button>
                          <button
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center space-x-2"
                          >
                            <Download className="w-4 h-4" />
                            <span>Download Report</span>
                          </button>
                          <button
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center space-x-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            <span>View on Stores</span>
                          </button>
                          <hr className="border-gray-700" />
                          <button
                            onClick={() => {
                              setShowDeleteModal(song.id);
                              setShowActionMenu(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 text-red-400 flex items-center space-x-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!showDeleteModal}
        onClose={() => setShowDeleteModal(null)}
        title="Delete Song"
      >
        <div className="space-y-4">
          <p className="text-gray-300">
            Are you sure you want to delete this song? This action cannot be undone and will remove 
            the song from all distribution platforms.
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(null)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => showDeleteModal && confirmDelete(showDeleteModal)}
            >
              Delete Song
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};